// Querverweise zwischen Artikeln.
//
// Im Markdown stehen sie als ganz normaler Link, nur mit eigenem Schema:
//   [Milchbildung](article:Im-Kreissaal/erste-anlegen)
// Ein eigenes Schema statt eines fertigen Pfads, weil dieselbe Textstelle an mehreren
// Stellen gerendert wird (`/user/...` für Leserinnen und in der Editor-Vorschau unter
// `/me` ganz ohne Leseansicht). Der Pfad entsteht deshalb erst beim Rendern aus dem
// aktuellen Basispfad, nicht schon beim Schreiben.

import { Link, useLocation } from 'react-router-dom'
import { createContext, useContext, type ReactNode } from 'react'
import { defaultUrlTransform } from 'react-markdown'

export const ARTICLE_LINK_SCHEME = 'article:'

export type ArticleLinkTarget = {
  chapterSlug: string
  articleSlug: string
}

export function buildArticleLinkHref({ chapterSlug, articleSlug }: ArticleLinkTarget): string {
  return `${ARTICLE_LINK_SCHEME}${chapterSlug}/${articleSlug}`
}

/** Liefert das Ziel eines `article:`-Links, oder null bei jedem anderen href. */
export function parseArticleLinkHref(href?: string): ArticleLinkTarget | null {
  if (!href || !href.startsWith(ARTICLE_LINK_SCHEME)) return null
  const [chapterSlug, articleSlug] = href.slice(ARTICLE_LINK_SCHEME.length).split('/')
  if (!chapterSlug || !articleSlug) return null
  return { chapterSlug, articleSlug }
}

/** Routen, unter denen die Leseansicht existiert (siehe App.tsx). */
const READING_BASE_PATHS = ['user']

export function articleReadingPath(
  basePath: string,
  { chapterSlug, articleSlug }: ArticleLinkTarget
): string {
  const params = new URLSearchParams({ chapter: chapterSlug, start: articleSlug })
  return `/${basePath}/articles/reading?${params.toString()}`
}

/**
 * Hervorhebung der verlinkten Wörter: dezent getönter Chip statt Unterstreichung, damit
 * ein Querverweis auf einen anderen Artikel im Fließtext sofort von einer externen
 * Quelle zu unterscheiden ist. `no-underline` und die explizite Farbe überschreiben die
 * `prose`-Vorgaben für `a`.
 */
export const ARTICLE_LINK_CLASS =
  'rounded px-1 -mx-0.5 bg-primary/10 text-primary font-medium no-underline ' +
  'hover:bg-primary/20 transition-colors ' +
  'dark:bg-white/15 dark:text-white dark:hover:bg-white/25'

const EXTERNAL_LINK_CLASS = 'text-primary underline hover:text-primary/80'

/**
 * Für die Content-Editor-Vorschau (`/me`): die liegt außerhalb der echten Leseansicht
 * und hat keine eigene Route für einzelne Artikel, sondern schaltet stattdessen intern
 * zwischen Kapitelliste, Kapitel- und Leseansicht um (siehe `PreparationPreview`). Ist
 * dieser Context gesetzt, ruft ein Klick auf einen `article:`-Link ihn statt eines
 * Router-Links auf, damit der verlinkte Artikel innerhalb der Vorschau erscheint, ohne
 * die Redaktion aus dem Editor zu werfen.
 */
export type ArticleLinkNavigate = (target: ArticleLinkTarget) => void
const ArticleLinkNavigateContext = createContext<ArticleLinkNavigate | null>(null)
export const ArticleLinkNavigateProvider = ArticleLinkNavigateContext.Provider

/**
 * `a`-Renderer für ReactMarkdown: interne Artikel-Querverweise werden zu Router-Links
 * (kein neuer Tab, kein Reload), alles andere bleibt ein externer Link.
 */
export function ArticleTextLink({ href, children }: { href?: string; children?: ReactNode }) {
  const { pathname } = useLocation()
  const navigateInPreview = useContext(ArticleLinkNavigateContext)
  const target = parseArticleLinkHref(href)

  if (!target) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={EXTERNAL_LINK_CLASS}>
        {children}
      </a>
    )
  }

  if (navigateInPreview) {
    return (
      <span
        role="link"
        tabIndex={0}
        onClick={() => navigateInPreview(target)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigateInPreview(target)
          }
        }}
        className={`${ARTICLE_LINK_CLASS} cursor-pointer`}
        title="Verweis auf einen anderen Artikel"
      >
        {children}
      </span>
    )
  }

  const basePath = pathname.split('/')[1] ?? ''
  if (!READING_BASE_PATHS.includes(basePath)) {
    // Vorschau im Content-Editor ohne Navigations-Context: die Hervorhebung soll man
    // sehen, aber ein Klick darf die Redaktion nicht aus dem Editor werfen.
    return (
      <span className={ARTICLE_LINK_CLASS} title="Verweis auf einen anderen Artikel">
        {children}
      </span>
    )
  }

  return (
    <Link to={articleReadingPath(basePath, target)} className={ARTICLE_LINK_CLASS}>
      {children}
    </Link>
  )
}

/**
 * `urlTransform`-Prop für ReactMarkdown: das Standard-Sanitizing kennt nur http(s)/mailto/etc.
 * und würde `article:`-Links zu einem leeren href machen - ArticleTextLink bekäme dann nie
 * das Ziel zu sehen und würde den Link als externen `<a href="" target="_blank">` rendern,
 * der beim Klick ein neues, leeres Fenster öffnet. Deshalb passiert das eigene Schema die
 * Sanitisierung unverändert, alles andere läuft weiter durch die Standardprüfung.
 */
export function markdownUrlTransform(url: string): string {
  if (url.startsWith(ARTICLE_LINK_SCHEME)) return url
  return defaultUrlTransform(url)
}

/** `components`-Prop für ReactMarkdown. */
export function markdownLinkComponents() {
  return { a: ArticleTextLink }
}
